import {
  Badge,
  Button,
  DropdownList,
  DropdownListItem,
  IconCheck20,
  IconKeyboardArrowDown20,
  Input,
  Spinner,
} from '@lightspeed/unified-components-helios-theme/react'
import { T, useT } from '@transifex/react'
import React, { useEffect, useId, useRef } from 'react'
import {
  AVAILABILITY_BADGE_APPEARANCES,
  AvailabilityBadgeAppearance,
  OutletDropdownListProps,
  OutletOption,
  OutletSelectProps,
} from './OutletSelect.types'
import { hasOutletAvailability, useOutletSelect } from './useOutletSelect'

const OutletDropdownList = DropdownList as React.FC<OutletDropdownListProps>
const OutletTriggerButton = Button as React.FC<
  React.ComponentProps<typeof Button> & { 'aria-label': string }
>

const TRIGGER_WIDTH_CLASS = 'w-[324px]'

export const getAvailabilityBadgeAppearance = (
  available: number
): AvailabilityBadgeAppearance => {
  if (available <= 0) {
    return AVAILABILITY_BADGE_APPEARANCES.danger
  }

  if (available === 1) {
    return AVAILABILITY_BADGE_APPEARANCES.warning
  }

  return AVAILABILITY_BADGE_APPEARANCES.success
}

const AvailabilityBadge: React.FC<{ available: number }> = ({ available }) => {
  const appearance = getAvailabilityBadgeAppearance(available)

  return (
    <Badge appearance={appearance} customClasses={{ container: 'shrink-0' }}>
      <T
        _str="{available} available"
        available={available}
        _context="Popover header"
      />
    </Badge>
  )
}

const OutletSummary: React.FC<{
  outlet: OutletOption
  showAddress?: boolean
}> = ({ outlet, showAddress = false }) => (
  <span className="flex min-w-0 flex-1 items-center gap-2">
    <span className="min-w-0 flex-1">
      <span className="block truncate text-left typography-body-sm-emphasized text-fg-neutral-default">
        {outlet.name}
      </span>
      {showAddress && outlet.address && (
        <span className="mt-0.5 block truncate text-left typography-body-xs text-fg-neutral-soft">
          {outlet.address}
        </span>
      )}
    </span>
    {hasOutletAvailability(outlet) && (
      <AvailabilityBadge available={outlet.available} />
    )}
  </span>
)

export const OutletSelect: React.FC<OutletSelectProps> = ({
  outlets,
  selectedOutletId,
  onOutletChange,
  disabled = false,
  isLoading = false,
}) => {
  const searchInputId = useId()
  const t = useT()
  const portalTargetRef = useRef<HTMLDivElement | null>(null)
  const emptyLabel = t('No outlets available', {
    _context: 'Message displayed when there are no outlets available.',
  })
  const noResultsLabel = t('No outlets found', {
    _context: 'Message displayed when no outlets match the search term.',
  })
  const loadingLabel = t('Loading outlets', {
    _context: 'Message displayed when outlets are loading.',
  })

  useEffect(() => {
    // Portal the dropdown to the body so it escapes the fulfilment popover's
    // clipping/scroll container while keeping Helios base styles available.
    const portalTarget = document.createElement('div')
    portalTarget.className = 'uc-base font-general'
    document.body.appendChild(portalTarget)
    portalTargetRef.current = portalTarget

    return () => {
      portalTargetRef.current = null
      portalTarget.remove()
    }
  }, [])

  const {
    filteredOutlets,
    handleOutletSelect,
    isDisabled,
    isEmpty,
    items,
    outletById,
    searchTerm,
    selectedOutlet,
    setSearchTerm,
    triggerOutlet,
  } = useOutletSelect({
    disabled,
    emptyLabel,
    isLoading,
    loadingLabel,
    onOutletChange,
    outlets,
    selectedOutletId,
  })

  const renderFooterSlot = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center p-3">
          <Spinner />
        </div>
      )
    }

    if (filteredOutlets.length === 0) {
      return (
        <div className="p-3 text-center typography-body-sm text-fg-neutral-soft">
          {isEmpty ? emptyLabel : noResultsLabel}
        </div>
      )
    }

    return undefined
  }

  return (
    <div className="uc-base inline-flex font-general">
      <OutletDropdownList
        items={items}
        portalTargetRef={portalTargetRef}
        positioning={{ placement: 'bottom-start', gutter: 4, flip: false }}
        trapFocus={false}
        onSelect={handleOutletSelect}
        customClasses={{
          positioner: `${TRIGGER_WIDTH_CLASS} wr-outlet-select-dropdown-positioner`,
          container: `${TRIGGER_WIDTH_CLASS} rounded-lg border border-border-neutral-soft bg-bg-neutral-top shadow-default`,
          header: 'border-b border-border-neutral-soft p-2',
          content: 'max-h-[260px] overflow-y-auto p-1',
        }}
        headerSlot={
          <div className="w-full">
            <div
              className="w-full px-4"
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
            >
              <Input
                ids={{ mainControl: searchInputId }}
                type="search"
                value={searchTerm}
                labelSlot={t('Search outlets', {
                  _context: 'Search input label for outlet picker.',
                })}
                placeholder={t('Enter outlet name', {
                  _context: 'Search input placeholder',
                })}
                onChange={setSearchTerm}
                size="small"
                customClasses={{
                  container: 'w-full',
                  label: 'sr-only',
                  controlContainer: 'w-full',
                  inputBlock: 'min-w-0 flex-1',
                  mainControl: 'w-full',
                }}
              />
            </div>
          </div>
        }
        triggerSlot={
          <OutletTriggerButton
            type="button"
            aria-label={t('Select outlet', {
              _context: 'Button aria label for outlet picker.',
            })}
            disabled={isDisabled}
            appearance="secondary"
            size="medium"
            customClasses={{
              container: `${TRIGGER_WIDTH_CLASS} h-[52px] min-h-[52px] justify-start gap-3 rounded-lg border border-border-go-default bg-bg-neutral-top px-3 py-0 text-fg-neutral-default shadow-stroke-neutral-soft hover:bg-bg-neutral-top hover:from-bg-neutral-top hover:to-bg-neutral-top active:bg-bg-neutral-top active:from-bg-neutral-top active:to-bg-neutral-top disabled:border-border-neutral-soft disabled:text-fg-neutral-soft`,
              content: 'min-w-0 flex-1',
              suffix: 'ml-0',
            }}
            suffixSlot={
              <span className="flex size-5 shrink-0 items-center justify-center text-fg-neutral-soft">
                {isLoading ? <Spinner /> : <IconKeyboardArrowDown20 />}
              </span>
            }
          >
            <OutletSummary outlet={triggerOutlet} />
          </OutletTriggerButton>
        }
        renderItem={({ item, state }) => {
          const outlet = outletById.get(item.value)

          if (!outlet) {
            return null
          }

          const isSelected = outlet.id === selectedOutlet?.id

          return (
            <DropdownListItem
              key={item.value}
              value={item.value}
              selected={isSelected}
              highlighted={state.highlighted}
              disabled={state.disabled}
              suffixSlot={
                isSelected && (
                  <span className="flex size-5 text-fg-go-default">
                    <IconCheck20 />
                  </span>
                )
              }
              customClasses={{
                container:
                  'min-h-[60px] items-center rounded-md px-2 py-2 hover:bg-bg-neutral-backdrop',
                content: 'min-w-0 flex-1',
                prefix: 'mr-2',
                suffix: 'ml-2',
              }}
            >
              <OutletSummary outlet={outlet} showAddress />
            </DropdownListItem>
          )
        }}
        renderSection={({ section, children }) => (
          <React.Fragment key={section.items[0]?.value ?? 'outlets'}>
            {children}
          </React.Fragment>
        )}
        footerSlot={renderFooterSlot()}
      />
    </div>
  )
}

export type { OutletOption } from './OutletSelect.types'
